import React, { useEffect, useState, useRef } from "react";
import "../../Css/account.css";
import { MdLockOutline } from "react-icons/md";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Form,
  Input,
  message,
  Tabs,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setUserToken } from "../../features/authSlice";
import {
  setProfile,
  storeToken,
} from "../../services/localStorageServices";
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import { AiOutlineUser, AiOutlineMail } from "react-icons/ai";
import { PRODUCT_API_BASE_URL } from "../../services/apiConfig";
import Logo from "../../Images/Logo/New Footer Logo.png";

export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { token } = useSelector((state) => state.auth);

  // =========================================================
  // CHECK LOGIN TOKEN
  // =========================================================

  useEffect(() => {
    if (token) {
      navigate("/");
    }
  }, [token, navigate]);

  // =========================================================
  // PASSWORD LOGIN
  // =========================================================

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${PRODUCT_API_BASE_URL}users/login`,
        {
          email: values.username,
          password: values.password,
        }
      );

      const { data } = response;

      if (data?.success) {
        dispatch(setUserToken({ token: data.token }));

        storeToken(data.token);

        setProfile(data.user);

        localStorage.setItem("token", data.token);

        localStorage.setItem(
          "user",
          JSON.stringify(data.user)
        );

        message.success(
          data.message || "Login successful."
        );

        navigate("/");
      } else {
        message.error(
          data?.message || "Login failed"
        );
      }
    } catch (error) {
      console.error("Login failed:", error);

      message.error(
        error?.response?.data?.message ||
          "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // OTP LOGIN STATES
  // =========================================================

  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const otpRefs = useRef([]);
  const [countdown, setCountdown] = useState(60);

  // OTP Terms checkbox
  const [otpTermsAccepted, setOtpTermsAccepted] =
    useState(false);

  // =========================================================
  // OTP COUNTDOWN
  // =========================================================

  useEffect(() => {
    let timer;

    if (showOtpField && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [showOtpField, countdown]);

  // =========================================================
  // SEND OTP
  // =========================================================

  const loginwithotp = async (values) => {
    setLoading(true);

    try {
      const email = values.email.trim();

      const response = await axios.post(
        `${PRODUCT_API_BASE_URL}users/login/send-otp`,
        {
          email,
        }
      );

      if (response.data?.success) {
        setOtpEmail(email);

        const resendSeconds =
          Number(response.data.resend_after) || 60;

        setCountdown(resendSeconds);

        setOtp("");

        // Reset OTP terms checkbox
        setOtpTermsAccepted(false);

        message.success(
          response.data.message ||
            "OTP sent successfully!"
        );

        setShowOtpField(true);

        return true;
      } else {
        message.error(
          response.data?.message ||
            "Failed to send OTP"
        );
      }
    } catch (error) {
      console.error(
        "OTP Request Failed:",
        error
      );

      message.error(
        error?.response?.data?.message ||
          "OTP request failed"
      );
    } finally {
      setLoading(false);
    }

    return false;
  };

  // =========================================================
  // VERIFY OTP
  // =========================================================

  const handleOtpSubmit = async () => {
    // Check Terms & Conditions
    if (!otpTermsAccepted) {
      message.error(
        "Please accept the Terms & Conditions and Privacy Policy."
      );

      return;
    }

    // Check OTP
    if (otp.length !== 6) {
      message.error(
        "Please enter the 6-digit OTP."
      );

      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${PRODUCT_API_BASE_URL}users/login/verify-otp`,
        {
          email: otpEmail,
          otp,
        }
      );

      const data = response.data;

      if (data?.success && data.token) {
        dispatch(
          setUserToken({
            token: data.token,
          })
        );

        storeToken(data.token);

        setProfile(data.user);

        localStorage.setItem(
          "token",
          data.token
        );

        localStorage.setItem(
          "user",
          JSON.stringify(data.user)
        );

        message.success(
          data.message ||
            "OTP verified successfully!"
        );

        navigate("/");
      } else {
        message.error(
          data?.message || "Invalid OTP"
        );
      }
    } catch (error) {
      console.error(
        "OTP Submit Failed:",
        error
      );

      message.error(
        error?.response?.data?.message ||
          "OTP submission failed"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // RESEND OTP
  // =========================================================

  const handleResendCode = () => {
    loginwithotp({
      email: otpEmail,
    });
  };

  // =========================================================
  // OTP COUNTDOWN FORMAT
  // =========================================================

  const formattedCountdown =
    `${String(
      Math.floor(countdown / 60)
    ).padStart(2, "0")}:${String(
      countdown % 60
    ).padStart(2, "0")}`;

  // =========================================================
  // OTP INPUT CHANGE
  // =========================================================

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(
      /\D/g,
      ""
    );

    if (!value) return;

    const otpArr = otp.split("");

    otpArr[index] = value;

    setOtp(otpArr.join(""));

    if (
      index < 5 &&
      otpRefs.current[index + 1]
    ) {
      otpRefs.current[index + 1].focus();
    }
  };

  // =========================================================
  // OTP BACKSPACE
  // =========================================================

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const otpArr = [...otp];

      if (otpArr[index]) {
        otpArr[index] = "";

        setOtp(otpArr.join(""));
      } else if (index > 0) {
        otpRefs.current[index - 1].focus();

        otpArr[index - 1] = "";

        setOtp(otpArr.join(""));
      }
    }
  };

  // =========================================================
  // TAB ITEMS
  // =========================================================

  const tabItems = [
    {
      key: "email",

      label: "Password Login",

      children: (
        <Form
          name="login_email"
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            remember: true,
          }}
          requiredMark={false}
        >
          {/* =================================================
              EMAIL
          ================================================= */}

          <Form.Item
            name="username"
            label="Email Address"
            rules={[
              {
                required: true,
                message:
                  "Please input your email!",
              },
              {
                type: "email",
                message:
                  "Please enter a valid email!",
              },
            ]}
          >
            <Input
              prefix={
                <AiOutlineUser className="input-icon" />
              }
              placeholder="name@example.com"
              size="large"
            />
          </Form.Item>

          {/* =================================================
              PASSWORD
          ================================================= */}

          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: true,
                message:
                  "Please input your password!",
              },
            ]}
          >
            <Input.Password
              prefix={
                <MdLockOutline className="input-icon" />
              }
              placeholder="Enter your password"
              size="large"
              iconRender={(visible) =>
                visible ? (
                  <FaRegEye />
                ) : (
                  <FaRegEyeSlash />
                )
              }
            />
          </Form.Item>

          {/* =================================================
              REMEMBER + FORGOT PASSWORD
          ================================================= */}

          <div className="form-actions-row">
            <Form.Item
              name="remember"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>
                Remember me
              </Checkbox>
            </Form.Item>

            <Link
              to="/account/forgot-password"
              className="forgot-link"
            >
              Forgot password?
            </Link>
          </div>

          {/* =================================================
              TERMS & CONDITIONS
          ================================================= */}

          <Form.Item
            name="termsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "Please accept the Terms & Conditions and Privacy Policy."
                        )
                      ),
              },
            ]}
            style={{
              marginTop: 16,
              marginBottom: 0,
            }}
          >
            <Checkbox>
              By signing in, you agree to our{" "}
              <Link
                to="/terms-conditions"
                className="highlight-link"
              >
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy-policy"
                className="highlight-link"
              >
                Privacy Policy
              </Link>
              .
            </Checkbox>
          </Form.Item>

          {/* =================================================
              SIGN IN BUTTON
          ================================================= */}

          <Form.Item
            style={{
              marginTop: 24,
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              className="login-form-button"
              loading={loading}
              block
              size="large"
            >
              Sign In
            </Button>

            <div className="register-text">
              Don't have an account?{" "}
              <Link
                to="/account/sign-up"
                className="highlight-link"
              >
                Register now
              </Link>
            </div>
          </Form.Item>
        </Form>
      ),
    },

    // =========================================================
    // OTP LOGIN
    // =========================================================

    {
      key: "number",

      label: "OTP Login",

      children: (
        <>
          <Form
            name="login_number"
            layout="vertical"
            onFinish={(values) =>
              loginwithotp(values)
            }
            requiredMark={false}
          >
            {/* =================================================
                EMAIL
            ================================================= */}

            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                {
                  required: true,
                  message:
                    "Please input your email!",
                },
                {
                  type: "email",
                  message:
                    "Please enter a valid email!",
                },
              ]}
            >
              <Input
                prefix={
                  <AiOutlineMail className="input-icon" />
                }
                placeholder="name@example.com"
                size="large"
                disabled={showOtpField}
              />
            </Form.Item>

            {/* =================================================
                SEND OTP
            ================================================= */}

            {!showOtpField && (
              <>
                {/* TERMS CHECKBOX */}

                <Form.Item
                  name="termsAccepted"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Please accept the Terms & Conditions and Privacy Policy."
                              )
                            ),
                    },
                  ]}
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Checkbox>
                    By signing in, you agree to our{" "}
                    <Link
                      to="/terms-conditions"
                      className="highlight-link"
                    >
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy-policy"
                      className="highlight-link"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  style={{
                    marginTop: 24,
                  }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button"
                    loading={loading}
                    block
                    size="large"
                  >
                    Send Verification Code
                  </Button>
                </Form.Item>
              </>
            )}
          </Form>

          {/* =================================================
              OTP SECTION
          ================================================= */}

          {showOtpField && (
            <div className="otp-container">
              <p className="otp-instruction">
                Enter the 6-digit code sent to{" "}
                <span>{otpEmail}</span>
              </p>

              <p className="otp-help-text">
                OTP not received? Check your
                Spam folder.
              </p>

              {/* OTP INPUTS */}

              <div className="otp-inputs">
                {[...Array(6)].map(
                  (_, index) => (
                    <Input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={
                        otp[index] || ""
                      }
                      onChange={(e) =>
                        handleOtpChange(
                          e,
                          index
                        )
                      }
                      onKeyDown={(e) =>
                        handleKeyDown(
                          e,
                          index
                        )
                      }
                      ref={(el) =>
                        (otpRefs.current[
                          index
                        ] = el)
                      }
                      className="otp-field"
                    />
                  )
                )}
              </div>

              {/* =================================================
                  TERMS CHECKBOX FOR FINAL OTP LOGIN
              ================================================= */}

              <div
                className="otp-terms-wrapper"
                style={{
                  marginTop: 16,
                  marginBottom: 16,
                }}
              >
                <Checkbox
                  checked={otpTermsAccepted}
                  onChange={(e) =>
                    setOtpTermsAccepted(
                      e.target.checked
                    )
                  }
                >
                  By signing in, you agree to
                  our{" "}
                  <Link
                    to="/terms-conditions"
                    className="highlight-link"
                  >
                    Terms & Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy-policy"
                    className="highlight-link"
                  >
                    Privacy Policy
                  </Link>
                  .
                </Checkbox>
              </div>

              {/* =================================================
                  VERIFY OTP
              ================================================= */}

              <Button
                type="primary"
                className="login-form-button"
                onClick={handleOtpSubmit}
                loading={loading}
                block
                size="large"
              >
                Verify & Sign In
              </Button>

              {/* =================================================
                  RESEND OTP
              ================================================= */}

              <div className="resend-wrapper">
                {countdown > 0 ? (
                  <span className="timer-text">
                    Resend code in{" "}
                    <strong>
                      {formattedCountdown}
                    </strong>
                  </span>
                ) : (
                  <span className="resend-text">
                    Didn't receive code?{" "}
                    <Button
                      type="link"
                      onClick={
                        handleResendCode
                      }
                      className="resend-btn"
                    >
                      Resend
                    </Button>
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      ),
    },
  ];

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div className="login-page">
      <div className="account-card">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="card-header">
          <div className="logo-wrapper">
            <img
              src={Logo}
              alt="Company Logo"
              className="brand-logo"
            />
          </div>

          <Breadcrumb
            className="custom-breadcrumb"
            items={[
              {
                title: (
                  <Link to="/">
                    Home
                  </Link>
                ),
              },
              {
                title: "Account Sign In",
              },
            ]}
          />
        </div>

        {/* =====================================================
            TABS
        ===================================================== */}

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);

            if (key !== "number") {
              setShowOtpField(false);
              setOtp("");
              setCountdown(60);

              // Reset OTP terms
              setOtpTermsAccepted(false);

              otpRefs.current = [];
            }
          }}
          items={tabItems}
          className="custom-tabs"
        />

      </div>
    </div>
  );
}